import { ALL_FILTER, GOOGLE, META } from "../utils/constants";
import { resolveCompetitorAdsPlatform } from "./competitorAdExternalUrls";

export const COMPETITOR_ADS_FROM = "competitorAds";
export const META_AD_LIBRARY_FROM = COMPETITOR_ADS_FROM;

const MIN_AD_IMAGE_DIMENSION = 150;
const COMPETITOR_ADS_PLATFORMS = new Set([META, GOOGLE]);

export const normalizeCompetitorName = (value) => {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.\-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const isGoogleSimgadUrl = (url) => {
  const lower = String(url || "").toLowerCase();
  return lower.includes("/simgad/") || lower.includes("/archive/simgad/");
};

/**
 * Formats active duration for competitor ad cards (hours if < 24h, otherwise days).
 * @returns {{ unit: "hours" | "days", value: number } | null}
 */
export const getMetaAdActiveDurationDisplay = (activeDurationHours) => {
  const hours = Number(activeDurationHours);
  if (!Number.isFinite(hours) || hours < 0) return null;

  if (hours < 24) {
    const value = Number.isInteger(hours) ? hours : Math.round(hours * 10) / 10;
    return { unit: "hours", value };
  }

  const days = hours / 24;
  const value = Number.isInteger(days) ? days : Math.round(days * 10) / 10;
  return { unit: "days", value };
};

/** Longest active duration first; ads without duration go last. */
export const compareMetaAdActiveDurationDesc = (a, b) => {
  const aHours = Number(a?.activeDurationHours);
  const bHours = Number(b?.activeDurationHours);
  const aFinite = Number.isFinite(aHours);
  const bFinite = Number.isFinite(bHours);

  if (aFinite && bFinite) return bHours - aHours;
  if (aFinite) return -1;
  if (bFinite) return 1;
  return 0;
};

export const isCompetitorAdGalleryItem = (item, from) => {
  const resolvedFrom = from ?? item?.from;
  return (
    resolvedFrom === COMPETITOR_ADS_FROM || resolvedFrom === "metaAdLibrary"
  );
};

export const isMetaAdLibraryItem = isCompetitorAdGalleryItem;

export const isMetaAdLibraryViewOnlyItem = (item) => item?.type === "video";

const isValidCompetitorAdImage = (ad, { platform } = {}) => {
  if (!ad?.url || !Number.isFinite(Number(ad?.activeDurationHours))) {
    return false;
  }

  if (platform === GOOGLE && !isGoogleSimgadUrl(ad.url)) {
    return false;
  }

  const width = Number(ad?.width);
  const height = Number(ad?.height);

  if (
    Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0
  ) {
    return width >= MIN_AD_IMAGE_DIMENSION && height >= MIN_AD_IMAGE_DIMENSION;
  }

  return true;
};

const isValidCompetitorAdVideo = (ad) => {
  return Boolean(ad?.url) && Number.isFinite(Number(ad?.activeDurationHours));
};

const isValidCompetitorAdMedia = ({ type, platform, ...ad }) => {
  if (type === "video") {
    return isValidCompetitorAdVideo(ad);
  }

  return isValidCompetitorAdImage(ad, { platform });
};

const getAdsEntriesFromPageValue = (pageValue) => {
  if (!pageValue) return [];

  if (Array.isArray(pageValue)) {
    return pageValue.map((ad, adIndex) => [
      ad?.libraryID != null && String(ad.libraryID).trim() !== ""
        ? String(ad.libraryID).trim()
        : String(adIndex),
      ad,
    ]);
  }

  if (typeof pageValue !== "object") return [];

  const adsMap =
    pageValue.ads &&
    typeof pageValue.ads === "object" &&
    !Array.isArray(pageValue.ads)
      ? pageValue.ads
      : pageValue;

  return Object.entries(adsMap).filter(
    ([, ad]) => ad && typeof ad === "object" && !Array.isArray(ad)
  );
};

const mapCompetitorAdToGalleryItem = ({
  pageId,
  libraryID: mapKey,
  ad,
  platform,
}) => {
  const mediaUrl = typeof ad?.url === "string" ? ad.url.trim() : "";
  if (!mediaUrl) return null;

  const mediaType = ad?.type === "video" ? "video" : "image";
  const activeDurationHours = Number(ad?.activeDurationHours);
  const width = Number(ad?.width);
  const height = Number(ad?.height);

  const galleryAd = {
    url: mediaUrl,
    type: mediaType,
    activeDurationHours,
    width,
    height,
    platform,
  };

  if (!isValidCompetitorAdMedia(galleryAd)) return null;

  const libraryID = String(ad?.libraryID ?? mapKey).trim();
  if (!libraryID) return null;

  const competitor =
    typeof ad?.competitor === "string" ? ad.competitor.trim() : "";
  const hasDimensions =
    Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0;

  return {
    id: libraryID,
    url: mediaUrl,
    type: mediaType,
    pageId,
    libraryID,
    platform,
    from: COMPETITOR_ADS_FROM,
    ...(hasDimensions ? { width, height } : {}),
    ...(competitor ? { competitor } : {}),
    activeDurationHours,
    ...(ad?.preview ? { preview: ad.preview } : {}),
    ...(ad?.isFavorite === true ? { isFavorite: true } : {}),
  };
};

export const isCompetitorAdFavorite = ({
  favoritesMap,
  pageId,
  libraryID,
  item = null,
}) => {
  const resolvedPageId = pageId ?? item?.pageId;
  const resolvedLibraryID = libraryID ?? item?.libraryID ?? item?.id;

  if (favoritesMap != null) {
    return Boolean(favoritesMap?.[resolvedPageId]?.[resolvedLibraryID]);
  }

  return item?.isFavorite === true;
};

export const buildCompetitorAdFavoritePayload = ({ pageId, libraryID, ad }) => {
  const platform = resolveCompetitorAdsPlatform({ pageValue: ad });

  return {
    id: libraryID,
    url: ad.url,
    platform,
    type: ad.type,
    pageId,
    libraryID,
    favoritedAt: Date.now(),
    ...(ad.competitor ? { competitor: ad.competitor } : {}),
    ...(ad.preview ? { preview: ad.preview } : {}),
    ...(Number.isFinite(Number(ad.activeDurationHours))
      ? { activeDurationHours: Number(ad.activeDurationHours) }
      : {}),
  };
};

export const hasCompetitorAdFavorites = (favoritesByPageId) => {
  if (!favoritesByPageId || typeof favoritesByPageId !== "object") {
    return false;
  }

  return Object.values(favoritesByPageId).some(
    (pageFavorites) =>
      pageFavorites &&
      typeof pageFavorites === "object" &&
      Object.keys(pageFavorites).length > 0
  );
};

export const formatCompetitorFavoritesForGallery = ({ favoritesByPageId }) => {
  if (!favoritesByPageId || typeof favoritesByPageId !== "object") {
    return [];
  }

  const items = [];

  Object.entries(favoritesByPageId).forEach(([pageId, pageFavorites]) => {
    if (!pageFavorites || typeof pageFavorites !== "object") return;

    Object.entries(pageFavorites).forEach(([libraryID, ad]) => {
      const mediaUrl = typeof ad?.url === "string" ? ad.url.trim() : "";
      if (!mediaUrl) return;

      const resolvedLibraryID = String(ad?.libraryID ?? libraryID).trim();
      if (!resolvedLibraryID) return;

      const mediaType = ad?.type === "video" ? "video" : "image";
      const competitor =
        typeof ad?.competitor === "string" ? ad.competitor.trim() : "";
      const activeDurationHours = Number(ad?.activeDurationHours);
      const platform = resolveCompetitorAdsPlatform({ pageValue: ad });

      items.push({
        id: resolvedLibraryID,
        url: mediaUrl,
        type: mediaType,
        pageId,
        libraryID: resolvedLibraryID,
        platform,
        from: COMPETITOR_ADS_FROM,
        isFavorite: true,
        favoritedAt: Number(ad?.favoritedAt) || 0,
        ...(competitor ? { competitor } : {}),
        ...(ad?.preview ? { preview: ad.preview } : {}),
        ...(Number.isFinite(activeDurationHours)
          ? { activeDurationHours }
          : {}),
      });
    });
  });

  return items.sort((a, b) => (b.favoritedAt ?? 0) - (a.favoritedAt ?? 0));
};

export const filterGalleryItemsByCompetitor = ({
  items,
  competitorFilterValue,
}) => {
  const filterValue = (competitorFilterValue || "").trim();
  if (!filterValue) return items;

  return items.filter(
    (item) => normalizeCompetitorName(item?.competitor) === filterValue
  );
};

export const filterGalleryItemsByPlatform = ({
  items,
  platformFilterValue,
}) => {
  const filterValue = String(platformFilterValue || ALL_FILTER)
    .trim()
    .toLowerCase();

  if (filterValue === ALL_FILTER) return items;

  return items.filter(
    (item) => resolveCompetitorAdsPlatform({ pageValue: item }) === filterValue
  );
};

/**
 * Flattens platform-grouped page maps into gallery items.
 * Expects `data: { meta?: pages, google?: pages }`.
 */
export const formatCompetitorAdsItemsForGallery = (pagesMap) => {
  if (!pagesMap || typeof pagesMap !== "object" || Array.isArray(pagesMap)) {
    return [];
  }

  const items = [];

  Object.entries(pagesMap)
    .filter(([platform]) => COMPETITOR_ADS_PLATFORMS.has(platform))
    .forEach(([platform, pageMap]) => {
      if (!pageMap || typeof pageMap !== "object" || Array.isArray(pageMap)) {
        return;
      }

      Object.entries(pageMap).forEach(([pageId, pageValue]) => {
        getAdsEntriesFromPageValue(pageValue).forEach(([libraryID, ad]) => {
          const galleryItem = mapCompetitorAdToGalleryItem({
            pageId,
            libraryID,
            ad,
            platform,
          });

          if (galleryItem) {
            items.push(galleryItem);
          }
        });
      });
    });

  return items.sort(compareMetaAdActiveDurationDesc);
};

export const formatMetaAdLibraryItemsForGallery =
  formatCompetitorAdsItemsForGallery;
